// re.rejouis.fr : scanner de code-barres (EAN) de la boîte, repris du lab
// (lab.rejouis/ean-scanner.js, 2026-10-13). Aucune dépendance npm :
//   const ean = await scanEan({ titre: 'Scanne le code-barres de la boîte' });
// → chaîne de chiffres, ou null si l'utilisateur ferme.
//
// Caméra en direct (getUserMedia) + BarcodeDetector natif (Chrome Android) ; ailleurs
// (iPhone, Firefox, Chrome bureau) le polyfill zxing-wasm est chargé à la demande.
// Repli si la caméra est refusée ou absente : photo, ou saisie des chiffres à la main.

const POLYFILL_URL = 'https://cdn.jsdelivr.net/npm/barcode-detector@3.2.2/ponyfill/+esm';
const FORMATS = ['ean_13', 'ean_8', 'upc_a'];
const LECTURES_CONFIRMEES = 2; // même code lu N fois d'affilée → anti-erreur de lecture

let detecteurPromise = null;
function detecteur() {
  if (!detecteurPromise) {
    detecteurPromise = (async () => {
      let Ctor = null;
      if ('BarcodeDetector' in window) {
        try {
          const sup = await window.BarcodeDetector.getSupportedFormats();
          if (sup.includes('ean_13')) Ctor = window.BarcodeDetector;
        } catch (_) { /* implémentation partielle → polyfill */ }
      }
      if (!Ctor) Ctor = (await import(/* @vite-ignore */ POLYFILL_URL)).BarcodeDetector;
      return new Ctor({ formats: FORMATS });
    })().catch((e) => { detecteurPromise = null; throw e; });
  }
  return detecteurPromise;
}

// Clé de contrôle GTIN (EAN-8, UPC-A, EAN-13, GTIN-14).
export function eanValide(code) {
  if (!/^\d{8}$|^\d{12,14}$/.test(code)) return false;
  const d = code.split('').map(Number);
  const cle = d.pop();
  const somme = d.reverse().reduce((s, n, i) => s + n * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (somme % 10)) % 10 === cle;
}

const CSS = `
.eans-ov{position:fixed;inset:0;z-index:99999;background:#000;display:flex;flex-direction:column;font-family:"Poppins",Helvetica,sans-serif;color:#fff}
.eans-ov button,.eans-ov input{font-family:inherit}
.eans-ov video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.eans-cadre{position:absolute;left:50%;top:42%;width:min(82vw,420px);height:min(30vw,150px);transform:translate(-50%,-50%);border-radius:14px;box-shadow:0 0 0 100vmax rgba(0,0,0,.55);border:3px solid rgba(255,255,255,.9);transition:border-color .15s}
.eans-cadre.ok{border-color:#3ddc84}
.eans-ligne{position:absolute;left:8%;right:8%;top:50%;height:2px;background:#F24444;box-shadow:0 0 8px #F24444;animation:eans-b 1.6s ease-in-out infinite}
@keyframes eans-b{0%,100%{transform:translateY(-38px)}50%{transform:translateY(38px)}}
.eans-haut{position:relative;display:flex;align-items:center;gap:10px;padding:max(14px,env(safe-area-inset-top)) 14px 10px}
.eans-titre{flex:1;font-size:16px;font-weight:600;text-shadow:0 1px 3px rgba(0,0,0,.6)}
.eans-btn{background:rgba(255,255,255,.16);color:#fff;border:0;border-radius:999px;padding:10px 14px;font-size:15px;font-weight:600;cursor:pointer;backdrop-filter:blur(6px)}
.eans-btn.on{background:#FFB804;color:#1F1973}
.eans-bas{position:absolute;left:0;right:0;bottom:0;padding:14px 16px max(18px,env(safe-area-inset-bottom));display:flex;flex-direction:column;gap:10px;align-items:center}
.eans-msg{font-size:14px;text-align:center;text-shadow:0 1px 3px rgba(0,0,0,.7);min-height:20px}
.eans-actions{display:flex;gap:10px;flex-wrap:wrap;justify-content:center}
.eans-saisie{display:none;gap:8px;width:100%;max-width:420px}
.eans-saisie.on{display:flex}
.eans-saisie input{flex:1;min-width:0;font-size:18px;padding:12px;border-radius:12px;border:0;letter-spacing:1px}
.eans-saisie button{background:#F24444;color:#fff;border:0;border-radius:12px;padding:0 18px;font-size:16px;font-weight:700}
`;

function el(tag, cls, html) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html != null) e.innerHTML = html;
  return e;
}

async function decoderImage(fichier) {
  const det = await detecteur();
  const bmp = await createImageBitmap(fichier);
  const codes = await det.detect(bmp);
  const bon = codes.map((c) => String(c.rawValue || '').trim()).find(eanValide);
  return bon || null;
}

export function scanEan(opts = {}) {
  const titre = opts.titre || 'Scanne le code-barres (EAN) de la boîte';
  return new Promise((resolve) => {
    if (!document.getElementById('eans-css')) {
      const st = el('style'); st.id = 'eans-css'; st.textContent = CSS; document.head.appendChild(st);
    }
    const ov = el('div', 'eans-ov');
    const video = el('video'); video.playsInline = true; video.muted = true; video.setAttribute('playsinline', '');
    const cadre = el('div', 'eans-cadre', '<div class="eans-ligne"></div>');
    const haut = el('div', 'eans-haut');
    const titreEl = el('div', 'eans-titre'); titreEl.textContent = titre;
    const btnLampe = el('button', 'eans-btn', '🔦'); btnLampe.style.display = 'none'; btnLampe.title = 'Lampe';
    const btnFermer = el('button', 'eans-btn', '✕'); btnFermer.title = 'Fermer';
    haut.append(titreEl, btnLampe, btnFermer);
    const bas = el('div', 'eans-bas');
    const msg = el('div', 'eans-msg', 'Ouverture de la caméra…');
    const actions = el('div', 'eans-actions');
    const btnPhoto = el('button', 'eans-btn', '📷 Photo');
    const btnClavier = el('button', 'eans-btn', '⌨️ Saisir les chiffres');
    actions.append(btnPhoto, btnClavier);
    const saisie = el('form', 'eans-saisie');
    const inp = el('input'); inp.inputMode = 'numeric'; inp.placeholder = '13 chiffres sous le code-barres'; inp.autocomplete = 'off';
    const ok = el('button', null, 'OK'); ok.type = 'submit';
    saisie.append(inp, ok);
    const fichier = el('input'); fichier.type = 'file'; fichier.accept = 'image/*'; fichier.capture = 'environment'; fichier.style.display = 'none';
    bas.append(msg, saisie, actions, fichier);
    ov.append(video, cadre, haut, bas);
    document.body.appendChild(ov);

    let flux = null, piste = null, fini = false, timer = null, dernier = null, compte = 0, lampe = false;

    function fermer(code) {
      if (fini) return;
      fini = true;
      clearTimeout(timer);
      if (flux) flux.getTracks().forEach((t) => t.stop());
      ov.remove();
      document.removeEventListener('keydown', surTouche);
      resolve(code || null);
    }
    function trouve(code) {
      cadre.classList.add('ok');
      if (navigator.vibrate) navigator.vibrate(60);
      setTimeout(() => fermer(code), 150);
    }
    function surTouche(e) { if (e.key === 'Escape') fermer(null); }
    document.addEventListener('keydown', surTouche);

    btnFermer.onclick = () => fermer(null);
    btnClavier.onclick = () => { saisie.classList.add('on'); actions.style.display = 'none'; inp.focus(); };
    inp.oninput = () => { delete inp.dataset.force; };
    saisie.onsubmit = (e) => {
      e.preventDefault();
      const code = inp.value.replace(/\D/g, '');
      if (code.length < 8) { msg.textContent = 'Il faut au moins 8 chiffres.'; return; }
      // Saisie manuelle : on prévient d'une faute de frappe probable sans bloquer.
      if (!eanValide(code) && !inp.dataset.force) { msg.textContent = 'Clé de contrôle incorrecte : vérifie les chiffres (OK pour forcer).'; inp.dataset.force = '1'; return; }
      fermer(code);
    };
    btnPhoto.onclick = () => fichier.click();
    fichier.onchange = async () => {
      const f = fichier.files && fichier.files[0];
      if (!f) return;
      msg.textContent = 'Lecture de la photo…';
      try {
        const code = await decoderImage(f);
        if (code) trouve(code);
        else msg.textContent = 'Code-barres non lu sur la photo : rapproche-toi, évite les reflets.';
      } catch (_) { msg.textContent = 'Lecture impossible : saisis les chiffres.'; }
      fichier.value = '';
    };
    btnLampe.onclick = async () => {
      lampe = !lampe;
      try { await piste.applyConstraints({ advanced: [{ torch: lampe }] }); btnLampe.classList.toggle('on', lampe); }
      catch (_) { btnLampe.style.display = 'none'; }
    };

    async function boucle(det) {
      if (fini) return;
      try {
        if (video.readyState >= 2) {
          const codes = await det.detect(video);
          const code = codes.map((c) => String(c.rawValue || '').trim()).find(eanValide);
          if (code) {
            compte = code === dernier ? compte + 1 : 1;
            dernier = code;
            if (compte >= LECTURES_CONFIRMEES) return trouve(code);
          }
        }
      } catch (_) { /* image transitoire illisible : on continue */ }
      timer = setTimeout(() => boucle(det), 90);
    }

    (async () => {
      const detPromise = detecteur(); // charge le polyfill pendant l'ouverture caméra
      detPromise.catch(() => {});
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('getUserMedia indisponible');
        flux = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (fini) { flux.getTracks().forEach((t) => t.stop()); return; }
        piste = flux.getVideoTracks()[0];
        try { await piste.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch (_) {}
        try { if (piste.getCapabilities?.().torch) btnLampe.style.display = ''; } catch (_) {}
        video.srcObject = flux;
        await video.play();
        msg.textContent = 'Place le code-barres dans le cadre';
      } catch (e) {
        cadre.style.display = 'none';
        msg.textContent = 'Caméra indisponible : prends une photo du code-barres ou saisis les chiffres.';
        return;
      }
      try { boucle(await detPromise); }
      catch (_) { msg.textContent = 'Lecteur de code-barres indisponible : saisis les chiffres.'; }
    })();
  });
}
